import type { LearnTopic, ResourceBlock } from "../topic-types";

type HubResource = {
  title: string;
  summary: string;
  text: string;
  points?: string[];
  note?: string;
  cta?: { text: string; label: string; to: string };
};

type HubSpec = Omit<LearnTopic, "sections"> & {
  understand: HubResource[];
  recognise: HubResource[];
  practise: HubResource[];
  reflect: HubResource[];
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function blocksFor(resource: HubResource, reflect = false): ResourceBlock[] {
  const blocks: ResourceBlock[] = [{ type: "prose", text: resource.text }];
  if (resource.points?.length) {
    blocks.push(
      reflect
        ? { type: "prompts", title: "Optional reflection prompts", items: resource.points }
        : { type: "list", title: "What may be helpful to know", items: resource.points },
    );
  }
  if (resource.note) blocks.push({ type: "note", text: resource.note });
  if (resource.cta) blocks.push({ type: "cta", ...resource.cta });
  return blocks;
}

export function createLearningHub(spec: HubSpec): LearnTopic {
  const makeResources = (
    resources: HubResource[],
    kind: "learn" | "practice" | "reflect" | "worksheet",
  ) =>
    resources.map((resource, index) => ({
      slug: slugify(resource.title),
      kind: index === resources.length - 1 && kind === "reflect" ? ("worksheet" as const) : kind,
      title: resource.title,
      summary: resource.summary,
      minutes: kind === "practice" ? 6 : kind === "reflect" ? 8 : 5,
      blocks: blocksFor(resource, kind === "reflect"),
      ...(index === resources.length - 1 && kind === "reflect"
        ? {
            worksheetFields: (resource.points ?? []).map((label) => ({ label, lines: 3 })),
          }
        : {}),
    }));

  return {
    ...spec,
    sections: [
      {
        slug: "understand",
        emoji: "📚",
        title: "Understand",
        blurb: "Plain-English foundations, with no pressure to relate them to yourself.",
        intro: "You can simply read. You do not need to revisit or disclose personal experiences.",
        resources: makeResources(spec.understand, "learn"),
      },
      {
        slug: "recognise",
        emoji: "🔎",
        title: "Recognise",
        blurb: "Possible patterns to notice without diagnosing or labelling yourself.",
        intro: "Recognition is information, not proof of a condition or a judgement about you.",
        resources: makeResources(spec.recognise, "learn"),
      },
      {
        slug: "practise",
        emoji: "🌿",
        title: "Practise",
        blurb: "Optional ways to support choice, steadiness and present-day safety.",
        intro: "These are invitations, not instructions. Pause, adapt or stop whenever you choose.",
        resources: makeResources(spec.practise, "practice"),
      },
      {
        slug: "reflect",
        emoji: "✍️",
        title: "Reflect",
        blurb: "Private prompts and a worksheet for your own pace.",
        intro: "You can keep answers brief, skip questions, or use these with professional support.",
        resources: makeResources(spec.reflect, "reflect"),
      },
    ],
  };
}